import IconButton from "../kit/IconButton";
import { IoClose } from "react-icons/io5";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import closeIcon from "../../assets/icons/closeIcon.png";
import { useData } from "../../context/DataContext/DataProvider";
import { loadIcon } from "../../util/iconRegistry";
// import "./TagInput.css";

const TagInput = forwardRef(
  (
    { tags, copy, onTagAdd, onTagDelete, label, defaultValue, placeHolder, placeHolderNote, maxTags = 3, validationError, selectedCategory , className, counterFormat = "default" },
    ref
  ) => {
    const [value, setValue] = useState(defaultValue || "");
    const [tagsValue, setTagsValue] = useState(tags || []);
    const [focused, setFocused] = useState(false);
    const { globalConfig } = useData();
    const knownServices = globalConfig?.knownServices || {};
    // console.log("knownServices ", knownServices);
    // forward values and a way to change them and get them
    useImperativeHandle(ref, () => ({
      value,
      setValue: (val) => setTagsValue(val),
      getValue: () => tagsValue,
    }));

    useEffect(() => {
      if (tags) {
        setTagsValue(tags);
      }
    }, [tags]);

    // useEffect(() => {
    //   console.log(tagsValue);
    // }, [tagsValue]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      
      // Check if a space was just typed (for Android compatibility)
      // Android keyboards use IME (keyCode 229) which breaks onKeyDown detection
      if (newValue.includes(' ') || newValue.includes('\t')) {
        // Extract the text before the space/tab
        const textBeforeSpace = newValue.split(/[\s\t]/)[0].trim();
        
        if (textBeforeSpace && tagsValue.length < maxTags && !tagsValue?.includes(textBeforeSpace)) {
          const cleanedValue = textBeforeSpace
            .replace(/[^a-zA-Z0-9]/g, "")
            .replace(/\s+/g, "")
            .trim();
          
          if (cleanedValue) {
            handleTagAdd(cleanedValue);
            setValue("");
            return; // Don't update with the space
          }
        }
        // If tag wasn't added, clear the space anyway
        setValue(textBeforeSpace);
        return;
      }
      
      setValue(newValue);
    };

    const handleTagAdd = (val) => {
      // console.log(val);
      let newTags = [...(tagsValue || [])];

      // Check if we've reached the maximum number of tags
      if (newTags.length >= maxTags) {
        return; // Don't add more tags if limit is reached
      }

      if (!tagsValue?.includes(val)) {
        setValue("");
        newTags.push(val);
        setTagsValue(newTags);
        onTagAdd && onTagAdd(val);
      }
    };

    // Real-time validation for tags
    const validateTag = (tag) => {
      if (!selectedCategory) return null;
      
      // Check if tag matches the selected category
      if (tag?.toLowerCase() === selectedCategory?.toLowerCase()) {
        return `"${tag}" cannot be used as a tag because it matches the selected category "${selectedCategory}"`;
      }
      
      return null;
    };

    // Get validation errors for current tags
    const getValidationErrors = () => {
      const errors = [];
      tagsValue?.forEach(tag => {
        const error = validateTag(tag);
        if (error) errors.push(error);
      });
      return errors;
    };

    const handleTagDelete = (tag) => {
      setTagsValue(tagsValue.filter((t) => t !== tag));
      onTagDelete && onTagDelete(tag);
    };

    const handleKeyDown = (e) => {
      // Skip handling for Android IME composition (keyCode 229)
      // Android uses onChange handler for space detection instead
      if (e.keyCode === 229 || e.key === "Unidentified" || e.key === "Process") {
        return;
      }
      
      if (
        e.key === "Enter" ||
        e.key === "Space" ||
        e.key === " " ||
        e.key === "Tab"
      ) {
        e.preventDefault();
        e.stopPropagation();

        // Check if we've reached the maximum number of tags
        if (tagsValue.length >= maxTags) {
          return; // Don't add more tags if limit is reached
        }

        if (value && !tagsValue?.includes(value)) {
          const cleanedValue = value
            .replace(/[^a-zA-Z0-9]/g, "")
            .replace(/\s+/g, "")
            .trim();
          
          if (cleanedValue) {
            handleTagAdd(cleanedValue);
            setValue("");
          }
        }
      } else if (e.key === "Backspace" && value === "") {
        tagsValue && handleTagDelete(tagsValue[tagsValue.length - 1]);
      }
    };

    const validationErrors = getValidationErrors();
    const hasErrors = validationError || validationErrors.length > 0;

    return (
      <div className="relative">
        <div className={`ui-input-wrapper flex gap-kit-10 relative justify-start flex-wrap items-center px-kit-24 py-kit-2 ${className || ""}`}>
        {label && (
          <label
            className={`ui-label absolute whitespace-nowrap bg-transparent duration-200 pointer-events-none ${
              focused || (value && value !== "") || placeHolder || tagsValue.length > 0
                ? ""
                : "translate-y-[100%]"
            }`}
            style={{
              top: copy ? "-1px" : "-20px",
              left: '0',
              transform: copy ? "translateY(-100%)" : "",
              fontSize: focused || (value && value !== "") || placeHolder || tagsValue.length > 0 ? '' : '1.1rem',
            }}
          >
            {label}
            {copy && <div className="ui-input-helper mt-kit-8 mb-kit-16">{copy}</div>}
          </label>
        )}
        {tagsValue &&
          tagsValue.map((tag, i) => {
            return (
              <span className="inyc-tag" key={i}>
                {
                  //if tag is a known service, render the icon
                  knownServices[tag?.toLowerCase()]?.icon ? (
                    <>
                      <div className="w-[1.1rem] h-[1.1rem] rounded-kit-full flex items-center justify-center overflow-hidden">
                        {loadIcon(knownServices[tag?.toLowerCase()]?.icon, {
                          size: 14,
                        })}
                      </div>
                      <div className="font-['Primary-Medium'] leading-[1rem]">
                        {knownServices[tag?.toLowerCase()]?.name}
                      </div>
                    </>
                  ) : (
                    tag
                  )
                }
                <IconButton label={`remove ${tag}`} size="xs" icon={<IoClose />} onClick={() => handleTagDelete(tag)} />
              </span>
            );
          })}
        <span className="relative min-w-0 flex-1">
        <input
          className="ui-input"
          placeholder={value || tagsValue?.length > 0 ? "" : (placeHolderNote ? "" : placeHolder)}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setFocused(false);
          }}
          value={value}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          style={{
            minWidth: "100px",
            width: "100%",
            flex: 1,
            minHeight: "35px",
          }}
        />
        {placeHolderNote && !value && tagsValue.length === 0 && (
          <span className="inyc-placeholder posting-tags-placeholder pointer-events-none absolute inset-0 flex items-center whitespace-nowrap">
            {placeHolder}{" "}
            <span className="posting-tags-placeholder-note">{placeHolderNote}</span>
          </span>
        )}
        </span>
        </div>
        <div className={`inyc-counter mt-kit-6 mr-kit-8 text-right ${tagsValue.length >= maxTags ? 'is-over' : ''}`}>
          {counterFormat === "compact" ? `${tagsValue.length}/${maxTags}` : `${tagsValue.length}/${maxTags} tags`}
        </div>
        
        {/* Real-time validation errors */}
        {hasErrors && (
          <div className="mt-kit-6 px-kit-8">
            <div className="text-danger opacity-80 text-kit-2xs font-['Primary-Regular']">
              {validationError || validationErrors.map((error, index) => (
                <div key={index} 
                className="text-danger opacity-80 text-kit-2xs font-['Primary-Regular']"
                >{error}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

const styles = {
  //   =====================
  tagInput: {
    display: "flex",
    gap: "var(--inyc-space-10)",
    position: "relative",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    alignItems: "center",
    padding: "var(--inyc-space-10) var(--inyc-space-20)",
    // border: "1px solid var(--inyc-divider)",
  },
  tag: {
    padding: "var(--inyc-space-10) var(--inyc-space-20)",
    borderRadius: "var(--inyc-radius-xl)",
    backgroundColor: "var(--inyc-tint)",
    // border: "1px solid var(--inyc-divider)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "var(--inyc-space-10)",
  },
  closeIcon: {
    color: "var(--inyc-primary-color)",
    transition: "0.3s",
    // backgroundColor: "rgba(0,0,0,0.1)",
    // border: "1px solid var(--inyc-divider)",
    // boxShadow: "var(--inyc-shadow-sm)",
    cursor: "pointer",
    borderRadius: "50%",
    height: 20,
    width: 20,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 5,
  },
  tagInnerInput: {
    minWidth: "100px",
    width: "100px",
    flex: 1,
    minHeight: "45px",
    backgroundColor: "var(--inyc-primary)",
    outline: "none",
    fontSize: 16,
    border: "none",
    // backgroundColor: "red",
  },
  smallLabel: {
    transform: "scale(0.9)",
    fontSize: "var(--inyc-text-lg)",
    padding: "0 var(--inyc-space-8)",
    transform: "translateY(-50%)",
    top: "-18px",
    left: "0px",
    color: "var(--inyc-primary-text-color)",
  },
};

export default TagInput;
